import { posthog } from "posthog-js"
import { marketingContext, ctaProperties } from "./marketing_attribution.js"

// The SDK also derives campaigns from stored initial/session URLs. Clean that
// ingress as well as our own attribution; keep the existing anonymous identity.
const CAMPAIGN_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "gad_source", "mc_cid", "gclid", "gclsrc", "dclid", "gbraid", "wbraid", "fbclid", "msclkid",
  "twclid", "li_fat_id", "igshid", "ttclid", "rdt_cid", "epik", "qclid", "sccid", "oppref",
  "bfcid", "irclid", "_kx", "ph_keyword", "$fbc", "$fbp"]
const campaignField = key => CAMPAIGN_FIELDS.some(name => key === name || key.endsWith(`_${name}`))
let enabled = false

export function cleanUrl(value) {
  if (value === "$direct") return value
  try { const url = new URL(value); return url.origin + url.pathname } catch { return undefined }
}

function cleanProperties(properties) {
  if (!properties) return
  for (const name of Object.keys(properties)) {
    if (campaignField(name)) { delete properties[name]; continue }
    if (name === "$referrer" || /(?:_url|_referrer)$/.test(name)) {
      const clean = cleanUrl(properties[name])
      if (clean) properties[name] = clean
      else delete properties[name]
    }
  }
}

export function cleanStoredAttribution(instance) {
  for (const name of CAMPAIGN_FIELDS) {
    for (const prefix of ["", "$initial_", "$entry_", "$session_entry_"]) {
      instance.unregister(prefix + name)
      instance.unregister_for_session(prefix + name)
    }
  }
  for (const name of ["$initial_campaign_params", "$initial_referrer_info", "$initial_person_info"]) {
    instance.unregister(name)
  }
  cleanStoredSession(instance)
}

function cleanStoredSession(instance) {
  const session = instance.get_property("$client_session_props")
  if (session?.props) {
    const props = { ...session.props }
    cleanProperties(props)
    for (const key of ["u", "r"]) {
      if (key in props) props[key] = cleanUrl(props[key]) || ""
    }
    if (JSON.stringify(props) !== JSON.stringify(session.props)) {
      instance.register({ $client_session_props: { ...session, props } })
    }
  }
}

export function sanitizeEvent(event) {
  if (!event) return event
  event.properties ||= {}
  for (const properties of [event.properties, event.$set, event.$set_once,
    event.properties.$set, event.properties.$set_once]) cleanProperties(properties)
  Object.assign(event.properties, marketingContext(), { source: "marketing" })
  const cta = ctaProperties(event.properties.cta_id)
  if (cta.cta_id !== "unannotated") event.properties.offer = cta.offer
  return event
}

export function captureMarketing(event, properties = {}) {
  if (!enabled) return
  try {
    posthog.capture(event, {
      ...marketingContext(), ...properties, page_path: window.location.pathname, schema_version: 1,
    })
  } catch { /* Analytics must never block a form or navigation. */ }
}

export default function setupAnalytics() {
  enabled = false
  const token = document.querySelector('meta[name="posthog-token"]')?.content
  const host = document.querySelector('meta[name="posthog-host"]')?.content
  if (!token || !host) return

  try { posthog.init(token, {
    api_host: host,
    defaults: "2026-08-30",
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: false,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: false,
      maskAllElementAttributes: false,
      maskTextSelector: "",
      recordHeaders: false,
      recordBody: false,
    },
    disable_surveys: true,
    capture_heatmaps: false,
    capture_performance: false,
    mask_all_text: false,
    mask_all_element_attributes: false,
    cross_subdomain_cookie: false,
    respect_dnt: true,
    save_campaign_params: false,
    save_referrer: false,
    disable_capture_url_hashes: true,
    mask_personal_data_properties: false,
    loaded: cleanStoredAttribution,
    before_send(event) {
      // A new session can refresh this SDK-owned URL record after initialization.
      try { cleanStoredSession(posthog) } catch { /* Outgoing sanitization still applies. */ }
      return sanitizeEvent(event)
    },
  }); enabled = true } catch { /* The website remains usable if initialization fails. */ }
}
